import { paths, SETTINGS_TAB_SEGMENTS, type SettingsTabKey } from '@/app/router/paths';
import type { IconName } from '@/design-system';
import { PERMISSIONS, type Permission } from '@/entities/user';

import { INVOICE_WITH_REPORT_ID, REMINDERS_ID } from './components/AutomationCard';
import { NHG_REGISTERED_AS_ID } from './components/NhgSettingsCard';
import { settingsTabsCopy } from './copy';

/**
 * De tabbladen van Instellingen, als gegevens.
 *
 * Zeven onderwerpen, in de volgorde waarin een keuringsbedrijf zich inricht: wie
 * het is, hoe het eruitziet, wat zijn rapport doet, wat zijn klanten krijgen,
 * met wie het werkt, en - het technische achteraan - op welk adres en met welke
 * software.
 *
 * - **Bedrijf** - de bedrijfsgegevens; het tegoed voor wie instellingen beheert.
 * - **Huisstijl** - logo en kleuren, naast het voorbeeld van voorblad en klantpagina.
 * - **Rapport** - wat een nieuwe rapportlink krijgt, de automatisering en de
 *   NHG-bijlage; waar de bedragen vandaan komen voor wie instellingen beheert.
 * - **Berichten** - de berichten aan klanten en de eigen mailserver.
 * - **Team** - de gebruikers en de openstaande uitnodigingen.
 * - **Domeinen** - de eigen domeinen.
 * - **Koppelingen** - sleutels voor de eigen software en AI-assistent, met de
 *   uitleg om te koppelen; optioneel. Alleen voor wie instellingen beheert: een sleutel werkt
 *   met de rechten van wie hem maakt.
 *
 * Wat je niet mag zien, bestaat niet: geen grijs tabblad, en het adres ervan
 * leidt naar het eerste tabblad. Net als de kaarten volgen de tabbladen de
 * rechten, niet de rol. Berichten vraagt `settings.manage` (zonder dat recht
 * staan de berichten er niet en toont de mailserver alleen dat hij van de
 * eigenaar is), Team vraagt `users.view` (zonder dat recht is de kaart leeg).
 */
export interface SettingsTab {
    key: SettingsTabKey;
    /** Het woord in de kop, en de kop boven de kaarten. */
    label: string;
    to: string;
    /** Het lijn-icoon voor het label. */
    icon: IconName;
}

const ORDER: readonly SettingsTabKey[] = [
    'company',
    'branding',
    'report',
    'messages',
    'team',
    'domains',
    'integrations',
];

const ICONS: Record<SettingsTabKey, IconName> = {
    company: 'company',
    branding: 'branding',
    /* Hetzelfde icoon als Rapport in de kop van een keuring. */
    report: 'reports',
    messages: 'messages',
    team: 'team',
    domains: 'domains',
    integrations: 'integrations',
};

/** Het recht zonder welk er op een tabblad niets te zien is. */
const REQUIRED: Partial<Record<SettingsTabKey, Permission>> = {
    messages: PERMISSIONS.settingsManage,
    team: PERMISSIONS.usersView,
    integrations: PERMISSIONS.settingsManage,
};

/** De tabbladen die deze persoon ziet, in de volgorde van de kop. Bedrijf is er altijd. */
export function settingsTabsFor(can: (permission: Permission) => boolean): SettingsTab[] {
    return ORDER.filter((key) => {
        const required = REQUIRED[key];

        return required === undefined || can(required);
    }).map((key) => ({
        key,
        label: settingsTabsCopy[key].label,
        to: paths.settingsTab(key),
        icon: ICONS[key],
    }));
}

/**
 * Het tabblad op dit adres: Bedrijf op `/instellingen`, Huisstijl op
 * `/instellingen/huisstijl`. `null` bij een onbekend of dieper pad en bij een
 * tabblad dat je niet mag zien. Hoofdletters en een slash aan het eind tellen
 * niet, net als in de router; de pagina zet het adres daarna recht.
 */
export function settingsTabAt(tabs: readonly SettingsTab[], pathname: string): SettingsTab | null {
    const path = pathname.toLowerCase().replace(/\/+$/, '');

    if (path !== paths.settings && !path.startsWith(`${paths.settings}/`)) {
        return null;
    }

    const segment = path.slice(paths.settings.length + 1);

    return tabs.find((tab) => SETTINGS_TAB_SEGMENTS[tab.key] === segment) ?? null;
}

/**
 * Plekken waar een link heen kan, met het tabblad waarop ze staan. Bedrijfsgegevens
 * linkt naar "Ingeschreven als", en dat staat bij de NHG-bijlage op Rapport.
 * Berichten linkt naar de schakelaars voor de factuur en de herinneringen, die
 * onder Automatisering op Rapport staan.
 */
const ANCHORS: Readonly<Record<string, SettingsTabKey>> = {
    [NHG_REGISTERED_AS_ID]: 'report',
    [INVOICE_WITH_REPORT_ID]: 'report',
    [REMINDERS_ID]: 'report',
};

/** Het tabblad waarop de plek uit `#…` staat, als je dat tabblad ziet; anders `null`. */
export function settingsTabForHash(tabs: readonly SettingsTab[], hash: string): SettingsTab | null {
    const key = ANCHORS[anchorId(hash)];

    return key === undefined ? null : (tabs.find((tab) => tab.key === key) ?? null);
}

/** `#nhg-ingeschreven-als` → `nhg-ingeschreven-als`; een kapotte codering blijft zoals hij is. */
export function anchorId(hash: string): string {
    const id = hash.startsWith('#') ? hash.slice(1) : hash;

    try {
        return decodeURIComponent(id);
    } catch {
        return id;
    }
}
